import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Minus, PackagePlus, CheckCircle2, AlertTriangle } from 'lucide-react';
import { InwardDropdownOption, OcrInvoiceItem } from '../../types';
import { ItemDropdown } from './ItemDropdown';
import { tg } from '../../telegram';
import { formatQty } from '../../utils/format';

interface AddInwardItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  inwardItems: InwardDropdownOption[];
  convertInwardItem: (
    name: string,
    packageUnit: string,
    quantity: number
  ) => {
    converted: boolean;
    standard_quantity: number;
    standard_unit: string;
    ratio: number;
    explanation?: string;
    error?: string;
  };
  onAdd: (item: OcrInvoiceItem) => void;
}

export const AddInwardItemModal: React.FC<AddInwardItemModalProps> = ({
  isOpen,
  onClose,
  inwardItems,
  convertInwardItem,
  onAdd,
}) => {
  const [selectedKey, setSelectedKey] = useState<string>('');
  const [selectedName, setSelectedName] = useState<string>('');
  const [packageUnit, setPackageUnit] = useState<string>('đơn vị');
  const [quantity, setQuantity] = useState<number>(1);

  // Reset form whenever modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedKey('');
      setSelectedName('');
      setPackageUnit('đơn vị');
      setQuantity(1);
    }
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || typeof document === 'undefined') return null;

  const handleSelectIngredient = (name: string, _unit?: string, option?: any) => {
    setSelectedName(name);
    if (option) {
      setSelectedKey(option.key || '');
      setPackageUnit(option.package_unit || 'đơn vị');
    } else {
      const matched = inwardItems.find(
        (it) => it.name.toLowerCase() === name.trim().toLowerCase()
      );
      if (matched) {
        setSelectedKey(matched.key || '');
        setPackageUnit(matched.package_unit || 'đơn vị');
      } else {
        setSelectedKey('');
      }
    }
  };

  const currentQty = Math.max(0, quantity);
  const conv = selectedName
    ? convertInwardItem(selectedName, packageUnit, currentQty)
    : null;

  const isValid = Boolean(selectedName.trim() && currentQty > 0);

  const handleConfirmAdd = () => {
    if (!isValid) return;

    tg.haptic.success();
    const finalConv = convertInwardItem(selectedName, packageUnit, currentQty);

    onAdd({
      name: selectedName.trim(),
      package_unit: packageUnit.trim(),
      quantity: currentQty,
      standard_unit: finalConv.standard_unit,
      standard_quantity: finalConv.standard_quantity,
      conversion_applied: finalConv.converted,
    });

    onClose();
  };

  const handleQuickAddQty = (delta: number) => {
    tg.haptic.impact('light');
    setQuantity((prev) => Math.max(1, prev + delta));
  };

  return createPortal(
    <div
      className="modal-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9990,
        backgroundColor: 'rgba(21, 17, 14, 0.65)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        className="modal-sheet"
        style={{
          width: '100%',
          maxWidth: 480,
          maxHeight: '90vh',
          backgroundColor: 'var(--bg-surface)',
          borderRadius: '20px 20px 0 0',
          padding: '16px 20px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          boxShadow: 'var(--shadow-lg)',
          overflowY: 'auto',
          animation: 'slideUp 0.2s ease-out',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Handle */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div
            style={{
              width: 40,
              height: 4,
              borderRadius: 2,
              backgroundColor: 'var(--border-color)',
            }}
          />
        </div>

        {/* Modal Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--primary-accent-subtle)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--primary-accent)',
              }}
            >
              <PackagePlus size={20} />
            </div>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>
                Thêm Món Nhập Kho
              </h3>
              <p style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                Chọn nguyên liệu & số lượng để thêm vào cuối phiếu
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="stepper-btn"
            style={{ width: 32, height: 32, padding: 0, color: 'var(--text-secondary)' }}
            title="Đóng"
          >
            <X size={18} />
          </button>
        </div>

        {/* Section 1: Chọn nguyên liệu */}
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 6 }}>
            1. Chọn nguyên liệu nhập (*)
          </label>
          <ItemDropdown
            value={selectedKey || selectedName}
            items={inwardItems}
            placeholder="🔍 Bấm để tìm & chọn nguyên liệu..."
            onChange={handleSelectIngredient}
            style={{ minHeight: 44, height: 44, fontSize: 14 }}
          />
        </div>

        {/* Section 2: Nhập số lượng & ĐVT */}
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 6 }}>
            2. Số lượng nhập ({packageUnit}) (*)
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* Stepper container */}
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                border: '1.5px solid var(--border-color)',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--bg-surface)',
                overflow: 'hidden',
                flex: 1,
                minHeight: 46,
              }}
            >
              <button
                type="button"
                onClick={() => handleQuickAddQty(-1)}
                className="stepper-btn"
                style={{ width: 44, height: 44, fontSize: 20 }}
                title="Giảm 1"
              >
                <Minus size={18} />
              </button>

              <input
                type="number"
                min="0.1"
                step="any"
                className="num"
                value={quantity === 0 ? '' : quantity}
                onChange={(e) => setQuantity(Math.max(0, Number(e.target.value)))}
                onBlur={(e) => {
                  if (!e.target.value) setQuantity(1);
                }}
                style={{
                  flex: 1,
                  border: 'none',
                  outline: 'none',
                  textAlign: 'center',
                  fontWeight: 800,
                  fontSize: 18,
                  color: 'var(--text-primary)',
                  backgroundColor: 'transparent',
                  padding: '4px',
                }}
              />

              <button
                type="button"
                onClick={() => handleQuickAddQty(1)}
                className="stepper-btn"
                style={{ width: 44, height: 44, fontSize: 20 }}
                title="Tăng 1"
              >
                <Plus size={18} />
              </button>
            </div>

            {/* ĐVT Badge */}
            <div
              className="badge badge-neutral"
              style={{
                minHeight: 46,
                padding: '0 14px',
                fontSize: 14,
                fontWeight: 800,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 'var(--radius-md)',
                whiteSpace: 'nowrap',
              }}
            >
              {packageUnit}
            </div>
          </div>

          {/* Quick preset increments */}
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            {[1, 5, 10, 20, 50].map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => {
                  tg.haptic.impact('light');
                  setQuantity(preset);
                }}
                style={{
                  flex: 1,
                  padding: '5px 0',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border-color)',
                  backgroundColor: quantity === preset ? 'var(--primary-accent)' : 'var(--bg-surface-subtle)',
                  color: quantity === preset ? '#FFFFFF' : 'var(--text-secondary)',
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                {preset}
              </button>
            ))}
          </div>
        </div>

        {/* Realtime Conversion Preview */}
        {selectedName && conv && (
          <div
            style={{
              padding: '12px 14px',
              borderRadius: 'var(--radius-md)',
              backgroundColor: conv.converted ? 'var(--color-success-bg)' : 'var(--color-danger-bg)',
              border: `1px solid ${conv.converted ? 'var(--color-success)' : 'var(--color-danger)'}`,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
            }}
          >
            {conv.converted ? (
              <CheckCircle2 size={18} color="var(--color-success)" style={{ flexShrink: 0, marginTop: 2 }} />
            ) : (
              <AlertTriangle size={18} color="var(--color-danger)" style={{ flexShrink: 0, marginTop: 2 }} />
            )}
            <div style={{ flex: 1, fontSize: 12, lineHeight: 1.4 }}>
              {conv.converted ? (
                <>
                  <div style={{ fontWeight: 800, color: 'var(--color-success)' }}>
                    Quy đổi chuẩn: +{formatQty(conv.standard_quantity, conv.standard_unit)}
                  </div>
                  <div style={{ color: 'var(--text-secondary)', marginTop: 2 }}>
                    Tỷ lệ: 1 {packageUnit} = {formatQty(conv.ratio, conv.standard_unit)}
                  </div>
                </>
              ) : (
                <div style={{ fontWeight: 700, color: 'var(--color-danger)' }}>
                  Chưa có trong 04_Quy_doi! Nhập vào Google Sheet rồi sync về.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Modal Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
          <button
            type="button"
            disabled={!isValid}
            onClick={handleConfirmAdd}
            className="btn btn-primary btn-full"
            style={{
              minHeight: 48,
              fontSize: 15,
              fontWeight: 700,
              gap: 8,
              opacity: isValid ? 1 : 0.5,
              cursor: isValid ? 'pointer' : 'not-allowed',
            }}
          >
            <Plus size={18} /> Thêm Vào Cuối Danh Sách
          </button>

          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary btn-full"
            style={{ minHeight: 42, fontSize: 13, fontWeight: 600 }}
          >
            Hủy Bỏ
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
